import React, { useState, useMemo, useRef } from 'react';
import { ChevronRight, Search, X, Sparkles, Loader2, Upload, FileText } from 'lucide-react';
import { GUIDELINES, GUIDELINE_CATEGORIES, type Guideline } from '../data/guidelines';

export interface UploadedGuidelineFile {
  id: string;
  name: string;
}

interface GuidelinePickerProps {
  selectedGuidelines: string[];
  onGuidelinesChange: (guidelineIds: string[]) => void;

  /** AI-suggested guideline IDs */
  suggestedGuidelineIds?: string[];
  suggestedGuidelineReasons?: Record<string, string[]>;
  onFindRelevant?: () => void;
  isFindingRelevant?: boolean;

  /** Custom guideline uploads */
  caseId?: string;
  onUploadGuideline?: (files: File[]) => void;
  uploadedGuidelineFiles?: UploadedGuidelineFile[];
  onRemoveUploadedGuideline?: (id: string) => void;
}

export const GuidelinePicker: React.FC<GuidelinePickerProps> = ({
  selectedGuidelines,
  onGuidelinesChange,
  suggestedGuidelineIds,
  onFindRelevant,
  isFindingRelevant,
  caseId,
  onUploadGuideline,
  uploadedGuidelineFiles,
  onRemoveUploadedGuideline,
}) => {
  const [guidelineSearch, setGuidelineSearch] = useState('');
  const [guidelineCategoryFilter, setGuidelineCategoryFilter] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleGuideline = (id: string) => {
    onGuidelinesChange(
      selectedGuidelines.includes(id)
        ? selectedGuidelines.filter(g => g !== id)
        : [...selectedGuidelines, id]
    );
  };

  const removeGuideline = (id: string) => {
    onGuidelinesChange(selectedGuidelines.filter(g => g !== id));
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const isSuggested = (id: string) => suggestedGuidelineIds?.includes(id);

  const groupedGuidelines = useMemo(() => {
    const filtered = GUIDELINES.filter(g => {
      const matchesSearch = !guidelineSearch ||
        g.title.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
        g.id.toLowerCase().includes(guidelineSearch.toLowerCase());
      const matchesCategory = !guidelineCategoryFilter || g.category === guidelineCategoryFilter;
      return matchesSearch && matchesCategory;
    });

    const groups: Record<string, Guideline[]> = {};
    for (const g of filtered) {
      if (!groups[g.category]) groups[g.category] = [];
      groups[g.category].push(g);
    }
    return groups;
  }, [guidelineSearch, guidelineCategoryFilter]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && onUploadGuideline) {
      onUploadGuideline(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs text-medical-500 dark:text-medical-400">
          Clinical Guidelines
        </label>
        <div className="flex items-center gap-1.5">
          {suggestedGuidelineIds && suggestedGuidelineIds.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const merged = new Set([...selectedGuidelines, ...suggestedGuidelineIds]);
                onGuidelinesChange(Array.from(merged));
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
            >
              Select all
            </button>
          )}
          {onFindRelevant && (
            <button
              type="button"
              onClick={onFindRelevant}
              disabled={isFindingRelevant}
              className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors disabled:opacity-50"
            >
              {isFindingRelevant ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Finding...</>
              ) : (
                <><Sparkles className="h-3 w-3" /> Find relevant</>
              )}
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-medical-400 dark:text-medical-500 mb-2 leading-relaxed">
        Select clinical guidelines relevant to this case. Selected guidelines are used to benchmark care in the
        <span className="font-medium text-medical-500 dark:text-medical-400"> Clinical Standards</span> and
        <span className="font-medium text-medical-500 dark:text-medical-400"> Causation</span> tabs,
        helping identify where treatment may have deviated from accepted practice.
      </p>

      {/* Search + Category filter */}
      <div className="flex gap-2 mb-2">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-medical-400" />
          <input
            type="text"
            value={guidelineSearch}
            onChange={(e) => setGuidelineSearch(e.target.value)}
            placeholder="Search guidelines..."
            className="input-field text-sm pl-8"
          />
        </div>
        <select
          value={guidelineCategoryFilter}
          onChange={(e) => setGuidelineCategoryFilter(e.target.value)}
          className="input-field text-sm w-48"
        >
          <option value="">All Categories</option>
          {GUIDELINE_CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Selected guidelines chips */}
      {selectedGuidelines.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedGuidelines.map(gId => {
            const g = GUIDELINES.find(gl => gl.id === gId);
            return g ? (
              <span key={gId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                {g.id}: {g.title.length > 40 ? g.title.substring(0, 40) + '...' : g.title}
                <button type="button" onClick={() => removeGuideline(gId)} className="hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : null;
          })}
        </div>
      )}

      {/* Categorised guideline list */}
      <div className="max-h-[280px] overflow-y-auto border border-medical-200 dark:border-medical-700 rounded-md">
        {Object.keys(groupedGuidelines).length === 0 ? (
          <div className="px-3 py-3 text-xs text-medical-400 text-center">No guidelines match your search</div>
        ) : (
          Object.entries(groupedGuidelines).map(([category, guidelines]) => {
            const isExpanded = expandedCategories.has(category) || !!guidelineSearch || !!guidelineCategoryFilter;
            const selectedInCategory = guidelines.filter(g => selectedGuidelines.includes(g.id)).length;
            const suggestedInCategory = guidelines.filter(g => isSuggested(g.id)).length;

            return (
              <div key={category}>
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-medical-700 dark:text-medical-300 bg-medical-50 dark:bg-medical-700/50 hover:bg-medical-100 dark:hover:bg-medical-700 border-b border-medical-200 dark:border-medical-700 sticky top-0 z-10"
                >
                  <ChevronRight className={`h-3 w-3 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                  <span>{category}</span>
                  <span className="text-medical-400 font-normal">({guidelines.length})</span>
                  {selectedInCategory > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-[10px]">
                      {selectedInCategory} selected
                    </span>
                  )}
                  {suggestedInCategory > 0 && selectedInCategory === 0 && (
                    <span className="ml-auto px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-[10px]">
                      {suggestedInCategory} recommended
                    </span>
                  )}
                </button>
                {isExpanded && (
                  <div className="divide-y divide-medical-100 dark:divide-medical-700">
                    {guidelines.map(g => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => toggleGuideline(g.id)}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colors flex items-center gap-1.5 ${
                          selectedGuidelines.includes(g.id)
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : isSuggested(g.id)
                            ? 'bg-purple-50/50 dark:bg-purple-900/10'
                            : ''
                        }`}
                      >
                        {isSuggested(g.id) && !selectedGuidelines.includes(g.id) && (
                          <Sparkles className="h-3 w-3 text-purple-400 flex-shrink-0" />
                        )}
                        <span className="font-medium flex-shrink-0">{g.id}</span>
                        <span className="text-medical-600 dark:text-medical-400 truncate">{g.title}</span>
                        <span className="text-medical-400 dark:text-medical-500 flex-shrink-0">({g.source})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* AI recommendation summary */}
      {suggestedGuidelineIds && suggestedGuidelineIds.length > 0 && (
        <p className="mt-1.5 text-[11px] text-purple-500 dark:text-purple-400 flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          {suggestedGuidelineIds.length} guidelines recommended based on your case data
        </p>
      )}

      {/* Upload custom guideline */}
      {caseId && onUploadGuideline && (
        <div className="mt-3 pt-3 border-t border-medical-200 dark:border-medical-700">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs text-medical-500 dark:text-medical-400">Custom Guideline Documents</label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 px-2 py-1 text-xs text-medical-600 dark:text-medical-400 hover:bg-medical-50 dark:hover:bg-medical-700 rounded transition-colors border border-medical-200 dark:border-medical-600"
            >
              <Upload className="h-3 w-3" /> Upload PDF
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
          {uploadedGuidelineFiles && uploadedGuidelineFiles.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {uploadedGuidelineFiles.map(file => (
                <span key={file.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded text-xs font-medium">
                  <FileText className="h-3 w-3" />
                  {file.name.length > 30 ? file.name.substring(0, 30) + '...' : file.name}
                  {onRemoveUploadedGuideline && (
                    <button type="button" onClick={() => onRemoveUploadedGuideline(file.id)} className="hover:text-red-600">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
